import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { DevUpdate } from "@/services/dev-updates.service";
import { TYPE_VARIANT, formatDate, dayKey } from "./dev-update-format";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function keyOf(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

// 6-week grid starting on the Sunday on/before the 1st of the month.
function buildGrid(month: Date): Date[] {
	const first = new Date(month.getFullYear(), month.getMonth(), 1);
	const start = new Date(first);
	start.setDate(first.getDate() - first.getDay());
	return Array.from({ length: 42 }, (_, i) => {
		const d = new Date(start);
		d.setDate(start.getDate() + i);
		return d;
	});
}

export default function CalendarView({ updates }: { updates: DevUpdate[] }) {
	const [month, setMonth] = useState(() => new Date());
	const [selected, setSelected] = useState<string | null>(null);

	const byDay = useMemo(() => {
		const map = new Map<string, DevUpdate[]>();
		for (const u of updates) {
			const key = dayKey(u.date);
			const list = map.get(key);
			if (list) list.push(u);
			else map.set(key, [u]);
		}
		return map;
	}, [updates]);

	const grid = useMemo(() => buildGrid(month), [month]);
	const todayKey = keyOf(new Date());
	const monthLabel = month.toLocaleDateString(undefined, {
		month: "long",
		year: "numeric",
	});

	const selectedItems = selected ? (byDay.get(selected) ?? []) : [];

	function shiftMonth(delta: number) {
		setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
		setSelected(null);
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h4 className="text-sm font-semibold text-foreground">{monthLabel}</h4>
				<div className="flex items-center gap-1">
					<Button variant="outline" size="sm" onClick={() => shiftMonth(-1)}>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							setMonth(new Date());
							setSelected(null);
						}}
					>
						Today
					</Button>
					<Button variant="outline" size="sm" onClick={() => shiftMonth(1)}>
						<ChevronRight className="h-4 w-4" />
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border border-border bg-border">
				{WEEKDAYS.map((w) => (
					<div
						key={w}
						className="bg-muted-subtle px-2 py-1 text-center text-xs font-medium text-muted"
					>
						{w}
					</div>
				))}
				{grid.map((d) => {
					const key = keyOf(d);
					const items = byDay.get(key) ?? [];
					const inMonth = d.getMonth() === month.getMonth();
					const isToday = key === todayKey;
					return (
						<button
							type="button"
							key={key}
							onClick={() => items.length && setSelected(key)}
							className={`min-h-[84px] bg-surface p-1.5 text-left align-top transition-colors ${
								inMonth ? "" : "opacity-40"
							} ${items.length ? "hover:bg-muted-subtle" : "cursor-default"} ${
								selected === key ? "ring-2 ring-primary ring-inset" : ""
							}`}
						>
							<div
								className={`mb-1 text-xs ${
									isToday
										? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground"
										: "text-muted"
								}`}
							>
								{d.getDate()}
							</div>
							<div className="space-y-0.5">
								{items.slice(0, 2).map((u) => (
									<div key={u.id} className="truncate">
										<Badge
											variant={TYPE_VARIANT[u.type] ?? "default"}
											className="w-full justify-start truncate text-[10px]"
										>
											{u.change}
										</Badge>
									</div>
								))}
								{items.length > 2 && (
									<div className="text-[10px] text-muted">
										+{items.length - 2} more
									</div>
								)}
							</div>
						</button>
					);
				})}
			</div>

			{selected && selectedItems.length > 0 && (
				<div className="rounded-md border border-border p-3">
					<h5 className="mb-2 text-sm font-semibold text-foreground">
						{formatDate(selectedItems[0].date)}
					</h5>
					<ul className="space-y-2">
						{selectedItems.map((u) => (
							<li
								key={u.id}
								className="flex items-start justify-between gap-2 border-b border-border pb-2 last:border-0 last:pb-0"
							>
								<div className="min-w-0">
									<div className="flex flex-wrap items-center gap-2">
										<Badge variant={TYPE_VARIANT[u.type] ?? "default"}>
											{u.type}
										</Badge>
										<span className="text-xs text-muted">{u.app}</span>
									</div>
									<p className="mt-1 font-medium text-foreground">{u.change}</p>
									{u.description && (
										<p className="mt-0.5 line-clamp-2 text-sm text-muted">
											{u.description}
										</p>
									)}
								</div>
								{u.url && (
									<a
										href={u.url}
										target="_blank"
										rel="noreferrer"
										className="shrink-0 text-muted hover:text-foreground"
									>
										<ExternalLink className="h-4 w-4" />
									</a>
								)}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}
